// src/pages/AdminDashboard.js
// Updated: 02/13/2026 - Added Device Management Link

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container, Typography, Grid, Card, CardActionArea, CardContent, Box,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChurchIcon from '@mui/icons-material/Church';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import MailIcon from '@mui/icons-material/Mail';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EditIcon from '@mui/icons-material/Edit';
import DevicesIcon from '@mui/icons-material/Devices'; // Added Icon for Devices

const AdminDashboard = () => {
  const { userProfile, churchProfile } = useAuth();

  const isAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';
  const canSendNotifications = isAdmin || userProfile?.canSendNotifications;
  const canViewInquiries = isAdmin || userProfile?.canViewInquiries;
  const canEditHomePage = isAdmin || userProfile?.canEditHomePage;
  const canManageEmailing = churchProfile?.enableEmailing && (isAdmin || userProfile?.canSendNotifications);
  const canManageSmtp = churchProfile?.enableEmailing && isAdmin;
  const canManageEventReminders = churchProfile?.enableEventReminders && isAdmin;

  const adminLinks = [
    {
      title: 'User Management',
      path: '/admin/users',
      icon: <AdminPanelSettingsIcon fontSize="large" />,
      description: 'Approve, edit, and manage all users in your church.',
      permission: isAdmin,
    },
    {
      title: 'Church Management',
      path: '/admin/churches',
      icon: <ChurchIcon fontSize="large" />,
      description: 'Edit your church’s details, branding, and features.',
      permission: isAdmin,
    },
    {
      title: 'Edit Home Page',
      path: '/?action=edit',
      icon: <EditIcon fontSize="large" />,
      description: 'Update the welcome message, images, and content on the home page.',
      permission: canEditHomePage,
    },
    {
      title: 'Send Push Notification',
      path: '/admin/send-notification',
      icon: <SendIcon fontSize="large" />,
      description: 'Send custom push notifications to your members.',
      permission: canSendNotifications,
    },
    {
      title: 'Notification History',
      path: '/admin/notification-history',
      icon: <HistoryIcon fontSize="large" />,
      description: 'View the history of all sent push notifications.',
      permission: isAdmin,
    },
    {
      title: 'Device Management', // NEW LINK ADDED HERE
      path: '/admin/devices',
      icon: <DevicesIcon fontSize="large" />,
      description: 'View active devices and manage push notification tokens.',
      permission: isAdmin,
    },
    {
      title: 'Member Inquiries',
      path: '/admin/inquiries',
      icon: <QuestionAnswerIcon fontSize="large" />,
      description: 'View and respond to inquiries from church members.',
      permission: canViewInquiries,
    },
    {
      title: 'Send Newsletter',
      path: '/admin/send-newsletter',
      icon: <MailIcon fontSize="large" />,
      description: 'Compose and send email newsletters to your members.',
      permission: canManageEmailing,
    },
    {
      title: 'Email Settings',
      path: '/admin/email-settings',
      icon: <SettingsIcon fontSize="large" />,
      description: 'Configure the SMTP settings for sending emails.',
      permission: canManageSmtp,
    },
    {
      title: 'Event Reminders',
      path: '/admin/event-reminders',
      icon: <AccessAlarmIcon fontSize="large" />,
      description: 'Set up automated reminders for upcoming events.',
      permission: canManageEventReminders,
    },
    {
      title: 'Distribution Lists',
      path: '/admin/distribution-lists',
      icon: <FormatListBulletedIcon fontSize="large" />,
      description: 'Manage mailing lists for targeted notifications.',
      permission: isAdmin,
    },
    {
      title: 'Volunteer Reports',
      path: '/admin/volunteer-reports',
      icon: <AssessmentIcon fontSize="large" />,
      description: 'View reports on volunteer hours and participation.',
      permission: isAdmin,
    },
  ];

  const filteredLinks = adminLinks.filter(link => link.permission);

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      <Grid container spacing={3}>
        {filteredLinks.map((link) => (
          <Grid item xs={12} sm={6} md={4} key={link.title}>
            <Card sx={{ height: '100%' }}>
              <CardActionArea component={RouterLink} to={link.path} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {link.icon}
                    <Typography variant="h6" component="div" sx={{ ml: 2 }}>
                      {link.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {link.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default AdminDashboard;